import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useSyncDisplay,
  subscribeSyncMonitor,
  getSyncMonitorState,
  deriveDisplay,
  type SyncDisplay,
} from '../../lib/syncMonitor'
import { retrySync, triggerSync } from '../../lib/sync'
import { liveQuery } from 'dexie'
import { db } from '../../lib/db'
// syncStatus.ts imports nothing, so the pill does not pull the chooser into the entry chunk (W11).
import { syncStatusWithConflicts } from '../../lib/syncStatus'
import { useReadOnly } from '../../lib/readOnly'
import { useQuotaFailure } from '../../lib/localWriteFailure'
import styles from './SyncPill.module.css'

/**
 * QUIL — global sync status pill (feature A).
 *
 * A quiet, app-wide indicator of OneDrive sync health, shown on every screen via
 * a single fixed mount in App. Healthy → renders nothing. It reads a derived
 * display from the dedicated sync monitor (useSyncExternalStore), so per-edit
 * churn re-renders only this pill, never the page. A companion toast offers a
 * one-tap retry when a write fails.
 *
 * States: saving (in-flight) · offline · error (write failed — tap to retry) ·
 * stale (reads persistently failing while writes are fine: tap to re-check) ·
 * conflict (W11: a chapter changed on two devices and waits for the writer's choice:
 * tap to open it). With any open conflict the pill never reads as healthy
 * (`syncStatusWithConflicts`); errors and offline still show first.
 */

/**
 * Copy per design contract v3 "Colours" §3: state a fact and stop. No ellipsis,
 * no apology. "Offline" and "Sync failed" both named a failure rather than the
 * situation — the writer's work is on the device and safe either way, and the
 * pill should say what is true rather than what went wrong.
 */
type PillState = SyncDisplay | 'conflict' | 'readonly' | 'quota'

const LABEL: Record<Exclude<PillState, 'healthy' | 'quota'>, string> = {
  saving: 'Saving',
  offline: 'Working from this device',
  error: 'Not yet reached OneDrive',
  stale: 'Not reading from OneDrive',
  conflict: 'Needs your choice',
  // W12 A5: this device's copy was saved by a newer Quil, or could not be upgraded safely. Nothing
  // is written to it and nothing syncs; the hint says which, and what to do.
  readonly: 'Read only',
}

const HINT: Partial<Record<PillState, string>> = {
  error: 'Retry syncing to OneDrive',
  stale: 'Re-check OneDrive',
  conflict: 'Open the chapter that changed on two devices',
  quota: 'Your last change is kept on this screen until space is freed. Storage in Settings says how.',
}

/** The oldest open conflict (W11: one at a time, oldest first), or null. */
function useOldestConflict(): { count: number; workId: string; chapterId: string } | null {
  const [open, setOpen] = useState<{ count: number; workId: string; chapterId: string } | null>(null)
  useEffect(() => {
    const sub = liveQuery(() => db.syncConflicts.filter(r => !r.resolved).toArray()).subscribe({
      next: rows => {
        const sorted = [...rows].sort((a, b) => a.detectedAt.localeCompare(b.detectedAt))
        const first = sorted[0]
        setOpen(first ? { count: rows.length, workId: first.workId, chapterId: first.id } : null)
      },
      error: () => setOpen(null),
    })
    return () => sub.unsubscribe()
  }, [])
  return open
}

export function SyncPill() {
  const health = useSyncDisplay()
  const conflict = useOldestConflict()
  const readOnly = useReadOnly()
  const quota = useQuotaFailure()
  // A read-only store never syncs, so a conflict or an error cannot be acted on: say so first.
  const display: PillState = readOnly ? 'readonly' : quota ? 'quota' : syncStatusWithConflicts(health, conflict?.count ?? 0)
  const navigate = useNavigate()
  const [retrying, setRetrying] = useState(false)
  const [toastOpen, setToastOpen] = useState(false)
  const prev = useRef<SyncDisplay>(deriveDisplay(getSyncMonitorState()))

  // Raise the retry toast the moment a genuine write failure first appears, and
  // drop it once we recover or start saving. Driven from the store subscription
  // (a change event, not a synchronous effect body) so it reacts to transitions.
  useEffect(() => {
    return subscribeSyncMonitor(() => {
      const next = deriveDisplay(getSyncMonitorState())
      if (next === 'error' && prev.current !== 'error') setToastOpen(true)
      else if (next === 'healthy' || next === 'saving') setToastOpen(false)
      prev.current = next
    })
  }, [])

  const runRetry = useCallback(async (mode: 'error' | 'stale') => {
    setRetrying(true)
    setToastOpen(false)
    try {
      if (mode === 'error') await retrySync()
      else await triggerSync()
    } catch {
      /* the monitor reflects the new outcome; nothing to do here */
    } finally {
      setRetrying(false)
    }
  }, [])

  // Don't pop in/out for the transient 'saving' state — the persistent per-page
  // save pills (Settings, wizard, writing surface) show that inline now (#4). This
  // floating pill is reserved for problems that need attention: offline/error/stale.
  if (display === 'healthy' || display === 'saving') return null

  const tappable = display === 'error' || display === 'stale' || display === 'conflict'
  const onTap = () => {
    if (display === 'conflict') {
      if (conflict) navigate(`/work/${conflict.workId}/write/${conflict.chapterId}`)
      return
    }
    if (display === 'error' || display === 'stale') void runRetry(display)
  }
  // W12 (design 3.6): a local write failed for want of space. The words are the canonical `QUOTA_STATUS`,
  // carried by the failure itself; the change that failed is still on the screen that made it.
  const label = display === 'quota' ? (quota?.status ?? '') : LABEL[display]
  const pillClass = styles.pill

  return (
    <>
      {/* One stable live region wraps the whole pill; the label change is polite. */}
      <div className={styles.wrap} role="status" aria-live="polite">
        {tappable ? (
          <button
            type="button"
            className={`${pillClass} ${styles.tappable}`}
            onClick={onTap}
            disabled={retrying}
            title={HINT[display]}
            data-testid="sync-pill"
            data-state={display}
          >
            <span aria-hidden="true" />
            {retrying ? 'Retrying' : label}
          </button>
        ) : (
          <span className={pillClass} data-testid="sync-pill" data-state={display} title={display === 'readonly' ? readOnly?.reason : HINT[display]}>
            <span aria-hidden="true" />
            {label}
          </span>
        )}
        {/* The pill answers "is sync OK?"; the next question is "which write, how
            long ago?" — one tap to the Settings sync-activity view. */}
        <button
          type="button"
          className={styles.details}
          onClick={() => navigate('/settings')}
          data-testid="sync-details"
          title="See sync activity in Settings"
        >
          Details
        </button>
      </div>

      {toastOpen && (
        <div className={styles.toast} role="alert" data-testid="sync-toast">
          <span aria-hidden="true" className={styles.toastDot} />
          <span className={styles.toastText}>Not yet reached OneDrive.</span>
          <button
            type="button"
            className={styles.toastBtn}
            onClick={() => void runRetry('error')}
            disabled={retrying}
          >
            Retry
          </button>
          <button
            type="button"
            className={styles.toastClose}
            onClick={() => setToastOpen(false)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </>
  )
}
